import { useEffect, useRef } from 'react';

const LineChart = ({
    data,
    title,
    ipType,
    globalLimits,
    discover = true,
    color,
    width = 260,  // Reduce width to allow 4 columns
    height = 250,  // Increase height from 200 to 250
    globalDate = null  // New prop for the gauge date
}) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        if (!data || Object.keys(data).length === 0) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // Set canvas size for high DPI displays
        const devicePixelRatio = window.devicePixelRatio || 1;
        canvas.width = width * devicePixelRatio;
        canvas.height = height * devicePixelRatio;
        canvas.style.width = width + 'px';
        canvas.style.height = height + 'px';
        ctx.scale(devicePixelRatio, devicePixelRatio);

        // Clear canvas (transparent background)
        ctx.clearRect(0, 0, width, height);

        // Convert data to array of points
        const entries = Object.entries(data)
            .map(([epoch, value]) => ({
                x: parseInt(epoch),
                y: parseFloat(value) || 0
            }))
            .sort((a, b) => a.x - b.x);

        if (entries.length === 0) {
            // Show "No data" message
            ctx.fillStyle = '#9ca3af';
            ctx.font = '12px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('No data available', width / 2, height / 2);
            return;
        }

        // Calculate margins (optimized for better space usage)
        const margin = { top: 25, right: 15, bottom: 35, left: 45 }; // Increase bottom margin for rotated labels
        const chartWidth = width - margin.left - margin.right;
        const chartHeight = height - margin.top - margin.bottom;

        // Draw chart area background (light gray background for the grid area)
        ctx.fillStyle = '#e0e5ebff'; // Light gray
        ctx.fillRect(margin.left, margin.top, chartWidth, chartHeight);

        // Use regional limits for Y-axis (now includes firstEpoch)
        const minX = Math.min(...entries.map(d => d.x));
        const maxX = Math.max(...entries.map(d => d.x));
        const minY = globalLimits.min || 0;
        const maxY = globalLimits.max || 100;

        // If there's a global date, draw differentiated background
        if (globalDate) {
            const globalTimestamp = Math.floor(globalDate.getTime() / 1000);
            if (globalTimestamp >= minX && globalTimestamp <= maxX) {
                const globalPixelX = margin.left + ((globalTimestamp - minX) / (maxX - minX)) * chartWidth;

                // Gray background for the left side (past)
                ctx.fillStyle = '#d4d8dcff';
                ctx.fillRect(margin.left, margin.top, globalPixelX - margin.left, chartHeight);

                // Lighter background for the right side (future)
                ctx.fillStyle = '#f0f2f4ff';
                ctx.fillRect(globalPixelX, margin.top, margin.left + chartWidth - globalPixelX, chartHeight);
            }
        }

        // Calculate nice Y-axis ticks
        const calculateNiceTicks = (min, max, targetSteps = 7) => {
            const range = max - min;
            if (range === 0) return { min, max, step: 1, ticks: [min] };

            // Calculate rough step size
            const roughStep = range / (targetSteps - 1);

            // Find a nice step size (round numbers)
            const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
            const normalizedStep = roughStep / magnitude;

            let niceStep;
            if (normalizedStep <= 1) niceStep = 1;
            else if (normalizedStep <= 2) niceStep = 2;
            else if (normalizedStep <= 5) niceStep = 5;
            else niceStep = 10;

            const step = niceStep * magnitude;

            // Calculate nice min and max
            const niceMin = Math.floor(min / step) * step;
            const niceMax = Math.ceil(max / step) * step;

            // Add 5% padding to the maximum value
            const paddedMax = niceMax + (niceMax - niceMin) * 0.05;

            // Generate tick values
            const ticks = [];
            for (let value = niceMin; value <= niceMax; value += step) {
                ticks.push(Math.round(value * 100) / 100); // Round to avoid floating point issues
            }

            return { min: niceMin, max: paddedMax, step, ticks };
        };

        const yAxisInfo = calculateNiceTicks(minY, maxY);
        const adjustedMinY = yAxisInfo.min;
        const adjustedMaxY = yAxisInfo.max;

        // Scaling functions (using adjusted Y values for better tick alignment)
        const scaleX = (x) => margin.left + ((x - minX) / (maxX - minX)) * chartWidth;
        const scaleY = (y) => margin.top + chartHeight - ((y - adjustedMinY) / (adjustedMaxY - adjustedMinY)) * chartHeight;

        // Find first non-zero value for discover functionality
        let firstNonZero = null;
        let discoverMessage = '';

        if (discover) {
            const firstEntry = entries[0];
            if (firstEntry.y > 0) {
                // First value is not zero
                firstNonZero = firstEntry;
                const firstDate = new Date(firstEntry.x * 1000);
                discoverMessage = `Present: ${(firstDate.getUTCMonth() + 1).toString().padStart(2, '0')}/${firstDate.getUTCDate().toString().padStart(2, '0')}/${firstDate.getUTCFullYear()}`;
            } else {
                // Find first non-zero value
                firstNonZero = entries.find(entry => entry.y > 0);
                if (firstNonZero) {
                    const firstDate = new Date(firstNonZero.x * 1000);
                    discoverMessage = `First: ${(firstDate.getUTCMonth() + 1).toString().padStart(2, '0')}/${firstDate.getUTCDate().toString().padStart(2, '0')}/${firstDate.getUTCFullYear()}`;
                }
            }
        }

        // Draw grid lines (white dashed)
        ctx.strokeStyle = 'white'; // White grids
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]); // Dashed lines

        // Vertical grid lines (time)
        const timeSteps = 9;
        for (let i = 0; i <= timeSteps; i++) {
            const x = margin.left + (i / timeSteps) * chartWidth;
            ctx.beginPath();
            ctx.moveTo(x, margin.top);
            ctx.lineTo(x, margin.top + chartHeight);
            ctx.stroke();
        }

        // Horizontal grid lines (values) - using calculated ticks
        yAxisInfo.ticks.forEach(tickValue => {
            const y = scaleY(tickValue);
            ctx.beginPath();
            ctx.moveTo(margin.left, y);
            ctx.lineTo(margin.left + chartWidth, y);
            ctx.stroke();
        });

        // Reset line dash for other elements
        ctx.setLineDash([]); // Reset dashed lines

        // Draw axes
        ctx.strokeStyle = '#333';
        ctx.lineWidth = 2;
        ctx.beginPath();
        // Y axis
        ctx.moveTo(margin.left, margin.top);
        ctx.lineTo(margin.left, margin.top + chartHeight);
        // X axis
        ctx.lineTo(margin.left + chartWidth, margin.top + chartHeight);
        ctx.stroke();

        // Draw Y axis labels (using calculated nice ticks)
        ctx.fillStyle = '#666';
        ctx.font = '9px Arial'; // Smaller font
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        yAxisInfo.ticks.forEach(tickValue => {
            const y = scaleY(tickValue);
            const displayValue = tickValue >= 1000 ?
                (tickValue / 1000).toFixed(tickValue % 1000 === 0 ? 0 : 1) + 'k' :
                Math.round(tickValue).toLocaleString();
            ctx.fillText(displayValue, margin.left - 5, y);
        });

        // Draw X axis labels (rotated dates in YY-MM format)
        ctx.save();
        ctx.fillStyle = '#666';
        ctx.font = '8px Arial'; // Smaller font for better space usage
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';

        for (let i = 0; i <= timeSteps; i++) {
            const epochValue = minX + (maxX - minX) * (i / timeSteps);
            const date = new Date(epochValue * 1000);
            const formattedDate = `${date.getFullYear().toString().slice(-2)}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
            const x = margin.left + (i / timeSteps) * chartWidth;
            const y = margin.top + chartHeight + 15; // Increase separation from 10 to 15

            // Rotate text -45 degrees (15 degrees less than before)
            ctx.save();
            ctx.translate(x, y);
            ctx.rotate(-Math.PI / 4); // -45 degrees in radians (was -Math.PI / 3 = -60 degrees)
            ctx.fillText(formattedDate, 0, 0);
            ctx.restore();
        }
        ctx.restore();

        // Color based on IP type or custom color
        const lineColor = color || (ipType === 'ipv6' ? '#8b5cf6' : '#3b82f6');

        // Draw the line with step interpolation (horizontal 4px, vertical 1px)
        ctx.strokeStyle = lineColor;
        ctx.beginPath();

        if (entries.length === 1) {
            // Single point - draw a circle
            const point = entries[0];
            ctx.beginPath();
            ctx.arc(scaleX(point.x), scaleY(point.y), 4, 0, 2 * Math.PI);
            ctx.fillStyle = lineColor;
            ctx.fill();
        } else {
            // Multiple points - draw step line
            entries.forEach((point, index) => {
                const x = scaleX(point.x);
                const y = scaleY(point.y);

                if (index === 0) {
                    ctx.moveTo(x, y);
                } else {
                    // Step interpolation: horizontal line (4px thick), then vertical line (1px thick)
                    ctx.lineWidth = 4;
                    ctx.lineTo(x, scaleY(entries[index - 1].y)); // horizontal line
                    ctx.stroke();

                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.moveTo(x, scaleY(entries[index - 1].y));
                    ctx.lineTo(x, y); // vertical line
                    ctx.stroke();

                    ctx.lineWidth = 4;
                    ctx.beginPath();
                    ctx.moveTo(x, y);
                }
            });
            ctx.lineWidth = 4;
            ctx.stroke();

            // Draw points
            ctx.fillStyle = lineColor;
            entries.forEach(point => {
                ctx.beginPath();
                ctx.arc(scaleX(point.x), scaleY(point.y), 3, 0, 2 * Math.PI);
                ctx.fill();
            });
        }

        // Draw discover tooltip and arrow if enabled
        if (discover && firstNonZero) {
            const discoverX = scaleX(firstNonZero.x);
            const discoverY = scaleY(firstNonZero.y);

            // Draw arrow pointing to the first non-zero point
            ctx.fillStyle = '#ff6b35';
            ctx.strokeStyle = '#ff6b35';
            ctx.lineWidth = 2;

            // Arrow line from above the point
            const arrowStartY = discoverY - 25;
            ctx.beginPath();
            ctx.moveTo(discoverX, arrowStartY);
            ctx.lineTo(discoverX, discoverY - 8);
            ctx.stroke();

            // Arrow head
            ctx.beginPath();
            ctx.moveTo(discoverX, discoverY - 8);
            ctx.lineTo(discoverX - 4, discoverY - 15);
            ctx.lineTo(discoverX + 4, discoverY - 15);
            ctx.closePath();
            ctx.fill();

            // Draw tooltip text (optimized for smaller space)
            ctx.fillStyle = 'black';
            ctx.font = '9px Arial'; // Slightly smaller font
            const tooltipHeight = 14; // Reduced height
            const tooltipY = Math.max(margin.top + 5, arrowStartY - tooltipHeight - 3); // Less margin

            // Calculate X position to prevent it from going out of lateral margins
            const textWidth = ctx.measureText(discoverMessage).width;
            const safeX = Math.max(margin.left + textWidth / 2, Math.min(discoverX, margin.left + chartWidth - textWidth / 2));

            // DO NOT draw tooltip background, only the text
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(discoverMessage, safeX, tooltipY + tooltipHeight / 2);
        }

        // Draw title (closer to the chart)
        ctx.fillStyle = '#1f2937';
        ctx.font = 'bold 11px Arial'; // Slightly smaller font
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillText(title, width / 2, 5); // Reduce from 8 to 5

        // DO NOT draw statistics (removed Last: xxx | Max: xxx)

        // Draw red vertical line and tooltip if there's a global date
        if (globalDate) {
            const globalTimestamp = Math.floor(globalDate.getTime() / 1000);
            if (globalTimestamp >= minX && globalTimestamp <= maxX) {
                const globalPixelX = margin.left + ((globalTimestamp - minX) / (maxX - minX)) * chartWidth;

                // Red vertical line (thinner)
                ctx.strokeStyle = '#e74c3c';
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(globalPixelX, margin.top);
                ctx.lineTo(globalPixelX, margin.top + chartHeight);
                ctx.stroke();

                // Find the value corresponding to the date
                const interpolateValue = (timestamp) => {
                    // First check if there's an exact match
                    const exactMatch = entries.find(entry => entry.x === timestamp);
                    if (exactMatch) {
                        return exactMatch.y;
                    }

                    // Find the closest points
                    let beforePoint = null;
                    let afterPoint = null;

                    for (let i = 0; i < entries.length - 1; i++) {
                        if (entries[i].x <= timestamp && entries[i + 1].x >= timestamp) {
                            beforePoint = entries[i];
                            afterPoint = entries[i + 1];
                            break;
                        }
                    }

                    if (!beforePoint && entries.length > 0) {
                        // It's before the first point
                        return entries[0].y;
                    }
                    if (!afterPoint && entries.length > 0) {
                        // It's after the last point
                        return entries[entries.length - 1].y;
                    }
                    if (beforePoint && afterPoint) {
                        // Step interpolation (use previous value until change)
                        return beforePoint.y;
                    }

                    return 0;
                };

                const currentValue = interpolateValue(globalTimestamp);

                // Tooltip con formato mm/dd/yyyy: value
                const tooltipDate = new Date(globalTimestamp * 1000);
                const formattedDate = `${(tooltipDate.getMonth() + 1).toString().padStart(2, '0')}/${tooltipDate.getDate().toString().padStart(2, '0')}/${tooltipDate.getFullYear()}`;
                const tooltipText = `${formattedDate}: ${Math.round(currentValue)}`;
                ctx.font = '10px Arial';

                // Calculate tooltip dimensions (single line)
                const textWidth = ctx.measureText(tooltipText).width;
                const tooltipWidth = textWidth + 8;
                const tooltipHeight = 18; // Height for 1 line

                // Determine tooltip position (avoid going out of the chart)
                let tooltipX = globalPixelX + 6;

                if (tooltipX + tooltipWidth > margin.left + chartWidth) {
                    // Goes out on the right, align it to the left of the line
                    tooltipX = globalPixelX - tooltipWidth - 6;
                }

                // Position tooltip always at the top of the chart
                const tooltipY = margin.top + 5;

                // Fondo del tooltip con transparencia y border radius
                ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
                ctx.beginPath();
                ctx.roundRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight, 2);
                ctx.fill();

                // Tooltip text (single line)
                ctx.fillStyle = '#ffffff';
                ctx.textAlign = 'left';
                ctx.textBaseline = 'top';
                ctx.fillText(tooltipText, tooltipX + 4, tooltipY + 4);
            }
        }

    }, [data, title, ipType, globalLimits, discover, color, width, height, globalDate]);

    return (
        <canvas
            ref={canvasRef}
            style={{
                backgroundColor: 'transparent' // Fondo transparente
            }}
        />
    );
};

export default LineChart;
